import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Gauge,
  Loader2,
  PauseCircle,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getQueueHealth, type PerCampaignRow } from "@/lib/queue-health.functions";
import { getSystemHealth } from "@/lib/system-health.functions";
import type { SystemHealthResponse } from "@/lib/system-health.functions";
import { reprocessCampaignPending } from "@/lib/queue-reprocess.functions";

export const Route = createFileRoute("/_authenticated/queue-health")({
  component: QueueHealthPage,
  head: () => ({ meta: [{ title: "Queue Health — InboxPulse" }] }),
});

function QueueHealthPage() {
  const fetchHealth = useServerFn(getQueueHealth);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["queue-health"],
    queryFn: () => fetchHealth(),
    refetchInterval: 10_000,
  });

  const fetchSystemHealth = useServerFn(getSystemHealth);
  const { data: sysHealth } = useQuery({
    queryKey: ["system-health"],
    queryFn: () => fetchSystemHealth(),
    refetchInterval: 60_000,
  });

  const isAdminError = error instanceof Error && /forbidden|admin/i.test(error.message);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Queue health"
        description="Live status of the tracking pipeline. Refreshes every 10s."
        actions={
          <button
            onClick={() => refetch()}
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-accent transition disabled:opacity-50"
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        }
      />

      <HelpPanel
        title="Hilfe: Wie voll ist die Warteschlange?"
        what={
          <>
            Zeigt, wie viele Pixel-Treffer gerade <b>warten</b>, bis sie verarbeitet werden, und wie
            schnell der Stapel abgearbeitet wird.
          </>
        }
        why={
          <>
            Wenn der Stapel ständig wächst, kommen deine Öffnungen mit Verzögerung in der Statistik
            an — und du verstehst nicht, warum.
          </>
        }
        how={[
          <>
            Zahlen lesen: <b>Pending</b> = wartend, <b>Processing</b> = gerade dran, <b>Failed</b> =
            Fehler.
          </>,
          <>Aktualisiert sich automatisch alle 10 Sekunden.</>,
          <>Wachsende Warteschlange → Admin informieren, evtl. ist ein Worker abgestürzt.</>,
        ]}
        tips={[
          <>
            Failed-Zähler oben? Auf <b>Dead-letter</b> die liegen gebliebenen Events ansehen.
          </>,
        ]}
        glossary={[
          {
            term: "Worker",
            def: "Hintergrund-Programm, das Treffer aus der Warteschlange holt und verarbeitet.",
          },
          { term: "Throughput", def: "Wieviel pro Sekunde verarbeitet wird." },
        ]}
      />

      {isLoading && <Card className="p-6 text-muted-foreground">Loading queue health…</Card>}

      {sysHealth && <SystemHealthCard health={sysHealth} />}

      {error && (
        <Card className="p-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError ? "Admin access required" : "Failed to load queue health"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {error instanceof Error ? error.message : String(error)}
            </div>
          </div>
        </Card>
      )}

      {!isLoading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat
              icon={<PauseCircle className="size-4" />}
              label="Pending"
              value={data.counts.pending}
            />
            <Stat
              icon={<Loader2 className="size-4" />}
              label="Processing"
              value={data.counts.processing}
            />
            <Stat
              icon={<XCircle className="size-4 text-destructive" />}
              label="Failed"
              value={data.counts.failed}
              tone={data.counts.failed > 0 ? "danger" : "neutral"}
            />
            <Stat
              icon={<Gauge className="size-4 text-emerald-500" />}
              label="Total in queue"
              value={data.counts.total}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Stat
              icon={<Gauge className="size-4" />}
              label="Drained (last 5m)"
              value={data.drain.events_drained_5m}
              hint={`${data.drain.runs_5m} runs`}
            />
            <Stat
              icon={<Gauge className="size-4" />}
              label="Drained (last 1h)"
              value={data.drain.events_drained_1h}
              hint={`${data.drain.runs_1h} runs`}
            />
            <Stat
              icon={<Clock className="size-4" />}
              label="Avg drain duration"
              value={data.drain.avg_duration_ms_1h ?? 0}
              suffix="ms"
              hint={
                data.drain.max_duration_ms_1h != null
                  ? `max ${data.drain.max_duration_ms_1h}ms`
                  : "—"
              }
            />
            <Stat
              icon={<AlertTriangle className="size-4 text-destructive" />}
              label="Failed runs (1h)"
              value={data.drain.failed_runs_1h}
              tone={data.drain.failed_runs_1h > 0 ? "danger" : "neutral"}
              hint={
                data.drain.last_run_at
                  ? `last ${new Date(data.drain.last_run_at).toLocaleTimeString()}`
                  : "no runs"
              }
            />
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
              <h2 className="text-sm font-medium">Per-campaign health</h2>
              <span className="text-xs text-muted-foreground">
                {data.perCampaign.length} campaigns
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Campaign</th>
                    <th className="text-right px-4 py-2 font-medium">Pending</th>
                    <th className="text-right px-4 py-2 font-medium">Processing</th>
                    <th className="text-right px-4 py-2 font-medium">Failed</th>
                    <th className="text-right px-4 py-2 font-medium">Throughput / 1h</th>
                    <th className="text-right px-4 py-2 font-medium">Avg drain ms</th>
                    <th className="text-right px-4 py-2 font-medium">p95 ms</th>
                    <th className="text-right px-4 py-2 font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.perCampaign.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        No queue or event activity.
                      </td>
                    </tr>
                  )}
                  {data.perCampaign.map((row) => (
                    <CampaignRow key={row.campaign_id} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="text-xs text-muted-foreground mt-4">
            Generated at {new Date(data.generatedAt).toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}

function CampaignRow({ row }: { row: PerCampaignRow }) {
  const qc = useQueryClient();
  const reprocess = useServerFn(reprocessCampaignPending);
  const [busy, setBusy] = useState(false);
  const mutation = useMutation({
    mutationFn: () => reprocess({ data: { campaignId: row.campaign_id } }),
    onMutate: () => setBusy(true),
    onSettled: () => setBusy(false),
    onSuccess: (res) => {
      toast.success(
        `Reprocess ausgelöst: ${res.requeuedFailed} failed + ${res.unstuckProcessing} stuck → pending. ` +
          `Drain claimed ${res.drain.claimed ?? 0}, inserted ${res.drain.inserted ?? 0}.`,
      );
      qc.invalidateQueries({ queryKey: ["queue-health"] });
      qc.invalidateQueries({ queryKey: ["queue-health-widget"] });
    },
    onError: (e) => {
      toast.error(`Reprocess fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
    },
  });
  return (
    <tr className="border-t border-border/50">
      <td className="px-4 py-2">
        <div className="font-medium truncate max-w-[260px]">{row.campaign_name ?? "(unnamed)"}</div>
        <div className="font-mono text-[10px] text-muted-foreground truncate max-w-[260px]">
          {row.campaign_id}
        </div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{row.pending}</td>
      <td className="px-4 py-2 text-right tabular-nums">{row.processing}</td>
      <td
        className={cn(
          "px-4 py-2 text-right tabular-nums",
          row.failed > 0 && "text-destructive font-medium",
        )}
      >
        {row.failed}
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{row.throughput_1h}</td>
      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
        {row.avg_drain_latency_ms ?? "—"}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
        {row.p95_drain_latency_ms ?? "—"}
      </td>
      <td className="px-4 py-2 text-right">
        <button
          onClick={() => mutation.mutate()}
          disabled={busy || (row.pending === 0 && row.failed === 0 && row.processing === 0)}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border hover:bg-accent transition disabled:opacity-40 disabled:cursor-not-allowed"
          title="Failed + stuck-processing → pending und Drain sofort ausführen"
        >
          <RefreshCw className={cn("size-3", busy && "animate-spin")} />
          {busy ? "Läuft…" : "Reprocess"}
        </button>
      </td>
    </tr>
  );
}

function Stat({
  icon,
  label,
  value,
  suffix,
  hint,
  tone = "neutral",
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  suffix?: string;
  hint?: string;
  tone?: "neutral" | "danger";
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        {icon}
        {label}
      </div>
      <div
        className={cn(
          "text-3xl font-semibold tabular-nums",
          tone === "danger" && value > 0 && "text-destructive",
        )}
      >
        {value.toLocaleString()}
        {suffix && (
          <span className="text-base font-normal text-muted-foreground ml-1">{suffix}</span>
        )}
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}

function SystemHealthCard({ health }: { health: SystemHealthResponse }) {
  const allOk = health.ok;
  return (
    <Card
      className={cn(
        "p-5 mb-6",
        allOk ? "border-emerald-500/30 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5",
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        {allOk ? (
          <CheckCircle2 className="size-5 text-emerald-600" />
        ) : (
          <ShieldAlert className="size-5 text-destructive" />
        )}
        <div className="font-medium">
          {allOk ? "System-Secrets OK" : "System-Secrets unvollständig"}
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          geprüft {new Date(health.generated_at).toLocaleTimeString()}
        </div>
      </div>
      <ul className="space-y-2 text-sm">
        {health.secrets.map((s) => (
          <li key={s.name} className="flex items-start gap-2">
            {s.ok ? (
              <CheckCircle2 className="size-4 text-emerald-600 mt-0.5 shrink-0" />
            ) : (
              <XCircle className="size-4 text-destructive mt-0.5 shrink-0" />
            )}
            <div className="flex-1">
              <div className="font-mono text-xs">
                {s.name}{" "}
                <span className="text-muted-foreground">
                  ({s.configured ? `${s.length} chars` : "nicht gesetzt"}, min {s.min_length})
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{s.purpose}</div>
              {!s.ok && <div className="text-xs text-destructive mt-0.5">{s.hint}</div>}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
